/**
 * Every attribute name Pax records. What is true of a whole process goes on its resource; what is
 * true of one operation goes on its span. The telemetry docs' attribute table says what each means.
 */
export const PAX = {
  gameId: "pax.game.id",
  gameKind: "pax.game.kind",
  gameHeadRevision: "pax.game.head_revision",
  gameAfterRevision: "pax.game.after_revision",
  agentId: "pax.agent.id",
  agentRole: "pax.agent.role",
  agentStopRequested: "pax.agent.stop_requested",

  activityKind: "pax.activity.kind",
  activityLabel: "pax.activity.label",
  activityKey: "pax.activity.key",
  activityAnswering: "pax.activity.answering",
  activityReadThroughRevision: "pax.activity.read_through_revision",
  activityResultedIn: "pax.activity.resulted_in",
  contextNewlyVisible: "pax.context.newly_visible",
  activityOutcome: "pax.activity.outcome",
  attempt: "pax.attempt",

  idempotencyKey: "pax.idempotency_key",
  proposalDeltaCount: "pax.proposal.delta_count",
  proposalStatus: "pax.proposal.status",
  proposalThroughRevision: "pax.proposal.through_revision",
  proposalOperations: "pax.proposal.operations",
  proposalRejected: "pax.proposal.rejected",
  proposalIssues: "pax.proposal.issues",
  revision: "pax.revision",
  deadline: "pax.deadline",
  socketFrameType: "pax.socket.frame_type",
  socketCloseCode: "pax.socket.close_code",
  socketCloseReason: "pax.socket.close_reason",
  socketCloseClean: "pax.socket.close_clean",
  reconnectReason: "pax.reconnect.reason",
  reconnectAttempts: "pax.reconnect.attempts",
  connectReason: "pax.connect.reason",
  forkGameId: "pax.fork.game_id",
  action: "pax.action",

  conversationKind: "pax.conversation.kind",
  conversationLabel: "pax.conversation.label",
  conversationGeneration: "pax.conversation.generation",
  conversationCompacted: "pax.conversation.compacted",
  conversationPromptTokens: "pax.conversation.prompt_tokens",
  conversationSummaryTokens: "pax.conversation.summary_tokens",
  toolDescription: "pax.tool.description",
  toolQuery: "pax.tool.query",
  toolEventCount: "pax.tool.event_count",
  toolMessageCount: "pax.tool.message_count",
  toolSaidNothing: "pax.tool.said_nothing",
  backoffReason: "pax.backoff.reason",
  backoffMs: "pax.backoff.ms",
  graderAttempt: "pax.grader.attempt",

  modelCallId: "pax.model_call.id",
  modelTransport: "pax.model.transport",
  modelCostUsd: "pax.model.cost_usd",
  modelUpstreamProvider: "pax.model.upstream_provider",
  modelRegion: "pax.model.region",
  modelNativeFinishReasons: "pax.model.native_finish_reasons",
  modelUpstreamCostUsd: "pax.model.upstream_cost_usd",
  errorFault: "pax.error.fault",
  errorCode: "pax.error.code",
  outputErrorType: "pax.output_error.type",
  outputErrorMessage: "pax.output_error.message",
  streamFirstByte: "pax.stream.first_byte",
  streamFirstChunk: "pax.stream.first_chunk",
  streamReasoningStarted: "pax.stream.reasoning_started",
  streamReasoningDone: "pax.stream.reasoning_done",
  streamTextStarted: "pax.stream.text_started",
  streamToolCallStarted: "pax.stream.tool_call_started",
  streamToolArgumentsDone: "pax.stream.tool_arguments_done",
  streamFinish: "pax.stream.finish",
  streamUsage: "pax.stream.usage",

  evalRunId: "pax.eval.run.id",
  evalTrialId: "pax.eval.trial.id",
  evalRoundsPlayed: "pax.eval.rounds_played",
  evalScore: "pax.eval.score",

  riskEvent: "pax.risk.event",
  serviceCaller: "pax.service_caller",
  traceId: "pax.trace_id",
  telemetryRebuiltRoot: "pax.telemetry.rebuilt_root",
  telemetryDroppedAttributes: "pax.telemetry.dropped_attributes",
} as const;

/**
 * Names from the OpenTelemetry GenAI semantic conventions. The conventions are still in
 * Development, so every name Pax emits is pinned here.
 * https://github.com/open-telemetry/semantic-conventions-genai/blob/main/docs/gen-ai/gen-ai-spans.md
 */
export const GEN_AI = {
  operationName: "gen_ai.operation.name",
  providerName: "gen_ai.provider.name",
  agentName: "gen_ai.agent.name",
  conversationId: "gen_ai.conversation.id",
  toolName: "gen_ai.tool.name",
  toolCallId: "gen_ai.tool.call.id",
  toolCallResult: "gen_ai.tool.call.result",
  requestModel: "gen_ai.request.model",
  requestMaxTokens: "gen_ai.request.max_tokens",
  requestTemperature: "gen_ai.request.temperature",
  requestTopP: "gen_ai.request.top_p",
  requestStream: "gen_ai.request.stream",
  requestReasoningLevel: "gen_ai.request.reasoning.level",
  outputType: "gen_ai.output.type",
  responseId: "gen_ai.response.id",
  responseModel: "gen_ai.response.model",
  responseFinishReasons: "gen_ai.response.finish_reasons",
  responseTimeToFirstChunk: "gen_ai.response.time_to_first_chunk",
  usageInputTokens: "gen_ai.usage.input_tokens",
  usageOutputTokens: "gen_ai.usage.output_tokens",
  usageCacheReadInputTokens: "gen_ai.usage.cache_read.input_tokens",
  usageCacheWriteInputTokens: "gen_ai.usage.cache_write.input_tokens",
  usageReasoningOutputTokens: "gen_ai.usage.reasoning.output_tokens",
  inputMessages: "gen_ai.input.messages",
  outputMessages: "gen_ai.output.messages",
  openaiResponseServiceTier: "openai.response.service_tier",
} as const;
